import { addDays, format, subMinutes } from "date-fns";
import { compareNullsLast, orderingDir, orderingField } from "@/shared/lib/ordering";
import type { Paginated, RenewalParams, RenewalRow, RenewalSortField } from "./types";
import { PAGE_SIZE } from "./types";

// Deterministic mock DB. `days_left` offsets are fixed; dates are derived from today so the
// rows always look current. user_id values match the customers mock (10435..10482) so the
// CustomerDrawer opens in mock mode too.
const NAMES = [
  "Ali Valiyev", "Dilnoza Karimova", "Jasur Toshmatov", "Malika Rahimova", "Sardor Yusupov",
  "Nodira Abdullayeva", "Bekzod Ergashev", "Shahlo Ismoilova", "Otabek Qodirov", "Zilola Nazarova",
  "Farhod Sobirov", "Madina Hasanova", "Rustam Aliyev", "Gulnora Mirzayeva", "Azizbek Tursunov",
  "Feruza Omonova", "Sherzod Ahmedov", "Kamola Saidova", "Umid Botirov", "Lola Xolmatova",
  "Doniyor Rashidov", "Sevara Normatova", "Ilhom Kamolov", "Nigora Salimova", "Timur Yoqubov",
];
const DAYS_LEFT = [
  0, 1, 2, 3, 5, 7, 9, 12, 14, 20, 25, 30, 4, 6, 10, 13,
  -1, -2, -5, -7, -10, -14, -20, -3, -28,
];

function ymd(offset: number): string {
  return format(addDays(new Date(), offset), "yyyy-MM-dd");
}

const DB: RenewalRow[] = NAMES.map((full_name, i) => {
  const days_left = DAYS_LEFT[i];
  const is_free = i % 3 !== 0;
  const is_repeatable = i % 2 === 0;
  const free_premiums_count = is_free ? (i % 4) : 0;
  const balance = is_free ? "0.00" : ((i * 61_500) % 400_000).toFixed(2);
  const active_agreements = (i * 5) % 17;
  const clients_count = (i * 11) % 60;
  const portfolio_som = active_agreements === 0 ? 0 : active_agreements * 3_250_000 + i * 10_000;
  // Every 6th merchant never logged in (matches last_active_day = null below).
  const neverLoggedIn = i % 6 === 5;
  return {
    user_id: 10482 - i,
    full_name,
    phone_number: `+998 9${i % 9} ${100 + ((i * 7) % 900)} ${10 + ((i * 3) % 90)} ${10 + ((i * 9) % 90)}`,
    premium_id: 500 + i,
    start_date: ymd(days_left - 180),
    end_date: ymd(days_left),
    days_left,
    is_free,
    is_repeatable,
    will_renew: days_left < 0 ? null : is_repeatable && (free_premiums_count > 0 || Number(balance) >= 50_000),
    balance,
    free_premiums_count,
    paid_orders_count: is_free ? 0 : (i % 4) + 1,
    active_agreements,
    total_agreements: active_agreements + ((i * 3) % 9),
    clients_count,
    accepted_clients_count: Math.min(clients_count, Math.round(clients_count * ((i % 4) + 5) / 10)),
    portfolio_som: portfolio_som.toFixed(2),
    // One merchant simulates a missing USD rate (the UI falls back to so'm).
    portfolio_usd: i === 7 ? null : active_agreements === 0 ? "0.00" : (portfolio_som / 12_650 + (i % 3) * 180).toFixed(2),
    agreements_30d: i % 5,
    last_active_day: neverLoggedIn ? null : ymd(-(i % 9)),
    last_login_at: neverLoggedIn ? null : subMinutes(new Date(), (i % 9) * 1440 + i * 37).toISOString(),
    active_days_30: neverLoggedIn ? 0 : (i * 7) % 31,
  };
});

export function mockRenewals(params: RenewalParams): Paginated<RenewalRow> {
  const q = params.search.trim().toLowerCase();
  let rows = DB.filter((r) =>
    params.status === "expiring"
      ? r.days_left >= 0 && r.days_left <= params.days
      : r.days_left < 0 && r.days_left >= -params.days,
  );
  if (params.kind !== "all") rows = rows.filter((r) => r.is_free === (params.kind === "free"));
  if (q) {
    rows = rows.filter(
      (r) => r.full_name.toLowerCase().includes(q) || r.phone_number.replace(/\s/g, "").includes(q.replace(/\s/g, "")),
    );
  }
  const field = orderingField(params.ordering) as RenewalSortField;
  const dir = orderingDir(params.ordering);
  const sorted = [...rows].sort(
    (a, b) => compareNullsLast(sortValue(a, field), sortValue(b, field), dir) || a.premium_id - b.premium_id,
  );
  const start = (params.page - 1) * PAGE_SIZE;
  return {
    count: sorted.length,
    next: null,
    previous: null,
    results: sorted.slice(start, start + PAGE_SIZE),
  };
}

// Row value behind each backend ordering field (user__first_name sorts by the display name here).
function sortValue(r: RenewalRow, field: RenewalSortField): unknown {
  return field === "user__first_name" ? r.full_name : r[field];
}
